<template>
  <div>
    <div class="main-content" v-if="loader === 100">
      <div class="form-card">
        <ValidationObserver ref="formObserver" v-slot="{ validate }">
          <b-form @submit.prevent="handleSubmit(validate)" class="form-content">

            <div class="form-section">
              <ValidationProvider name="Título" rules="required" v-slot="{ errors }">
                <div class="form-group">
                  <label for="titulo" class="form-label">
                    <span class="required">*</span> Título do Manual
                  </label>
                  <b-form-input
                      id="titulo"
                      v-model="manual.titulo"
                      :state="errors.length ? false : null"
                      placeholder="Digite o título do manual"
                      class="form-input"
                  />
                  <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                </div>
              </ValidationProvider>
            </div>

            <div class="form-section">
              <div class="form-group">
                <label for="descricao" class="form-label">Descrição</label>
                <b-form-textarea
                    id="descricao"
                    v-model="manual.descricao"
                    rows="3"
                    placeholder="Digite uma descrição para o manual"
                    class="form-textarea"
                />
              </div>
            </div>

            <div class="form-section">
              <div class="form-group">
                <label class="form-label">Conteúdo</label>
                <div id="manual-editor-wrapper">
                  <ckeditor
                      ref="manualEditor"
                      :editor="editor"
                      v-model="editorData"
                      :config="editorConfig"
                      @ready="onEditorReady"
                      @input="onEditorInput"
                  ></ckeditor>
                </div>
                <small class="char-counter" :class="contentCounterClass">
                  {{ contentCharCount }} / {{ maxContentChars }} caracteres
                  <span v-if="contentExceeded"> — Limite excedido</span>
                </small>
              </div>
            </div>

            <div class="form-section">
              <div class="form-group">
                <label class="form-label">Grupos de Manual (opcional)</label>
                <v-select
                    v-model="manual.manual_grupos"
                    :options="manualGruposOptions"
                    :label="'path'"
                    :reduce="group => group.id"
                    multiple
                    class="form-select"
                    placeholder="Selecione um ou mais grupos"
                />
                <small class="text-muted" v-if="manual.manual_grupos && manual.manual_grupos.length">
                  {{ manual.manual_grupos.length }} grupo(s) selecionado(s)
                </small>
              </div>
            </div>

            <div class="form-section">
              <div class="form-group">
                <label for="arquivos" class="form-label">
                  <span class="required" v-if="!isEdit">*</span> Arquivos
                </label>
                <div class="file-upload-container">
                  <input
                      id="arquivos"
                      type="file"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.mp4,.avi,.mov,.wmv,.webm"
                      @change="handleFileUpload"
                      ref="manualFile"
                      class="form-file"
                      multiple
                  >
                  <small class="file-info">
                    <i class="fas fa-info-circle"></i>
                    Formatos aceitos: PDF, Word (doc/docx), Excel (xls/xlsx), PowerPoint (ppt/pptx), Vídeo (mp4/avi/mov/wmv/webm). Máx: 200MB
                  </small>
                </div>
              </div>
            </div>

            <div class="form-section" v-if="newFilesPreview.length">
              <label class="form-label">Novos arquivos para envio ({{ newFilesPreview.length }})</label>
              <div class="file-preview-list">
                <div class="file-preview" v-for="(file, index) in newFilesPreview" :key="`novo-${file.name}-${index}`">
                  <i :class="['fas', file.icon, 'fa-2x', 'me-2']"></i>
                  <div class="file-preview-info">
                    <div class="file-preview-name">{{ file.name }}</div>
                    <div class="file-preview-meta">
                      <span v-if="file.size">{{ file.size }}</span>
                    </div>
                  </div>
                  <b-button variant="outline-danger" size="sm" @click="removeSelectedFile(index)">
                    Remover
                  </b-button>
                </div>
              </div>
            </div>

            <div class="form-section" v-if="isEdit && existingFiles.length">
              <label class="form-label">Arquivos atuais ({{ existingFiles.length }})</label>
              <div class="file-preview-list">
                <div class="file-preview" v-for="file in existingFiles" :key="`atual-${file.id || file.arquivo}`">
                  <i :class="['fas', file.icon, 'fa-2x', 'me-2']"></i>
                  <div class="file-preview-info">
                    <div class="file-preview-name">{{ file.name }}</div>
                    <div class="file-preview-meta">
                      <span v-if="file.size">{{ file.size }}</span>
                      <a v-if="file.url" :href="file.url" target="_blank" class="ms-2">
                        <i class="fas fa-external-link-alt"></i> Abrir
                      </a>
                    </div>
                  </div>
                  <b-button variant="outline-danger" size="sm" @click="markExistingFileForRemoval(file)">
                    Remover
                  </b-button>
                </div>
              </div>
            </div>

            <div class="form-section">
              <label class="form-label">
                <span class="required">*</span> Unidades de destino
              </label>

              <div class="row mb-3">
                <div class="col-md-7">
                  <label class="form-label mb-1" style="font-weight:600; font-size:12px">Grupo (opcional)</label>
                  <v-select
                      v-model="selectedGrupoId"
                      :options="allGroups"
                      :label="'label'"
                      :reduce="reduceGrupo"
                      class="form-select"
                      placeholder="Selecionar grupo pré-cadastrado"
                      @input="onGrupoInput"
                      :clearable="true"
                  />
                  <small class="text-muted">
                    Ao escolher um grupo, as unidades associadas serão pré-selecionadas.
                  </small>
                </div>
                <div class="col-md-5">
                  <label class="form-label mb-1" style="font-weight:600; font-size:12px">Despacho geral</label>
                  <div class="dispatch-all-box">
                    <b-form-checkbox
                        v-model="dispatchAll"
                        switch
                        @change="onDispatchAll"
                    >
                      <strong>Todas as unidades ({{ todasUnidades.length }})</strong>
                    </b-form-checkbox>
                  </div>
                </div>
              </div>

              <div class="form-group">
                <v-select
                    v-model="manual.unidades"
                    :options="todasUnidades"
                    :label="'nome'"
                    :reduce="opt => opt.id"
                    multiple
                    class="form-select"
                    placeholder="Selecione uma ou mais unidades"
                />
                <small class="text-muted" v-if="manual.unidades && manual.unidades.length">
                  {{ manual.unidades.length }} unidade(s) selecionada(s)
                </small>
              </div>
            </div>

            <div class="form-section">
              <div class="row">
                <div class="col-md-4">
                  <ValidationProvider name="Status" rules="required" v-slot="{ errors }">
                    <div class="form-group">
                      <label for="status" class="form-label">
                        <span class="required">*</span> Status
                      </label>
                      <b-form-select
                          id="status"
                          v-model="manual.status"
                          :state="errors.length ? false : null"
                          class="form-select-status"
                      >
                        <option :value="1">Ativo</option>
                        <option :value="0">Inativo</option>
                      </b-form-select>
                      <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                    </div>
                  </ValidationProvider>
                </div>
              </div>
            </div>

            <div class="form-footer">
              <div class="required-info">
                <span class="required">*</span>
                <span>Campos obrigatórios</span>
              </div>
              <div class="form-actions">
                <b-button variant="outline-secondary" class="btn-cancel" @click="$router.go(-1)">
                  <i class="fas fa-arrow-left"></i> Voltar
                </b-button>
                <b-button
                    type="submit"
                    variant="primary"
                    class="btn-save"
                  :disabled="(!manual.arquivos.length && !isEdit) || isLoading || contentExceeded"
                >
                  <template v-if="isLoading">
                    <i class="fas fa-spinner fa-spin"></i> Salvando...
                  </template>
                  <template v-else>
                    <i class="fas fa-save"></i> Salvar
                  </template>
                </b-button>
              </div>
            </div>

          </b-form>
        </ValidationObserver>
      </div>
    </div>

    <div class="loading-container" v-else>
      <div class="loading-card">
        <div class="progress-container">
          <div class="progress">
            <div
                class="progress-bar"
                role="progressbar"
                :style="'width: ' + loader + '%;'"
            ></div>
          </div>
          <div class="loading-text">
            <div class="loading-title">Carregando...</div>
            <div class="loading-percentage">{{ loader }}%</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import vSelect from "vue-select";
import axios from "axios";
import CKEditor from "@ckeditor/ckeditor5-vue2";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic/build/ckeditor";
import {ValidationObserver, ValidationProvider} from "vee-validate";
import {BButton, BForm, BFormInput, BFormInvalidFeedback, BFormSelect, BFormTextarea, BFormCheckbox} from "bootstrap-vue";

const MAX_SIZE_MB = 200;
const MAX_CONTENT_CHARS = 1000;
const WARN_CONTENT_CHARS = Math.floor(MAX_CONTENT_CHARS * 0.8);
const ALLOWED_EXT = ["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "mp4", "avi", "mov", "wmv", "webm"];
const ICON_MAP = {
  pdf: "fa-file-pdf",
  doc: "fa-file-word",
  docx: "fa-file-word",
  xls: "fa-file-excel",
  xlsx: "fa-file-excel",
  ppt: "fa-file-powerpoint",
  pptx: "fa-file-powerpoint",
  mp4: "fa-file-video",
  avi: "fa-file-video",
  mov: "fa-file-video",
  wmv: "fa-file-video",
  webm: "fa-file-video",
};

export default {
  name: "manualForm",
  components: {
    vSelect,
    ValidationObserver,
    ValidationProvider,
    ckeditor: CKEditor.component,
    BButton,
    BForm,
    BFormInput,
    BFormTextarea,
    BFormSelect,
    BFormInvalidFeedback,
    BFormCheckbox,
  },
  data() {
    return {
      loader: 0,
      isLoading: false,
      manual: {
        titulo: "",
        descricao: "",
        conteudo: "",
        manual_grupos: [],
        arquivos: [],
        unidades: [],
        status: 1,
      },
      editor: ClassicEditor,
      editorInstance: null,
      editorData: "",
      editorConfig: {
        toolbar: [
          'heading',
          '|',
          'bold',
          'italic',
          'link',
          'bulletedList',
          'numberedList',
          '|',
          'blockQuote',
          'insertTable',
          'undo',
          'redo',
        ],
      },
      newFilesPreview: [],
      existingFiles: [],
      arquivosRemover: [],
      todasUnidades: [],
      allGroups: [],
      manualGruposOptions: [],
      selectedGrupoId: null,
      dispatchAll: false,
      isEdit: false,
      manualId: null,
      _suppressUnitWatch: false,
    };
  },
  watch: {
    'manual.unidades'(newVal) {
      if (this._suppressUnitWatch) return;
      if (this.dispatchAll && this.todasUnidades.length && newVal.length !== this.todasUnidades.length) {
        this.dispatchAll = false;
      }
    },
  },
  computed: {
    maxContentChars() {
      return MAX_CONTENT_CHARS;
    },
    contentCharCount() {
      const html = typeof this.editorData === "string" ? this.editorData : "";
      if (!html) return 0;
      const div = document.createElement("div");
      div.innerHTML = html;
      return (div.textContent || div.innerText || "").length;
    },
    contentExceeded() {
      return this.contentCharCount > MAX_CONTENT_CHARS;
    },
    contentCounterClass() {
      if (this.contentExceeded) return "counter-danger";
      if (this.contentCharCount >= WARN_CONTENT_CHARS) return "counter-warning";
      return "counter-normal";
    },
  },
  methods: {
    resetManualFileInput() {
      if (this.$refs.manualFile) {
        this.$refs.manualFile.value = "";
      }
    },

    handleFileUpload(event) {
      const selectedFiles = Array.from((event.target.files || []));
      if (!selectedFiles.length) return;

      const invalid = selectedFiles.find(file => {
        const ext = (file.name.split(".").pop() || "").toLowerCase();
        const invalidSize = file.size > MAX_SIZE_MB * 1024 * 1024;
        const invalidType = !ALLOWED_EXT.includes(ext);
        return invalidSize || invalidType;
      });

      if (invalid) {
        const ext = (invalid.name.split(".").pop() || "").toLowerCase();
        if (invalid.size > MAX_SIZE_MB * 1024 * 1024) {
          this.$message("Erro", `Arquivo '${invalid.name}' muito grande (máx: ${MAX_SIZE_MB}MB).`, "error");
        } else if (!ALLOWED_EXT.includes(ext)) {
          this.$message("Erro", `Formato não suportado no arquivo '${invalid.name}'.`, "error");
        }
        this.resetManualFileInput();
        return;
      }

      const mergedFiles = [...this.manual.arquivos, ...selectedFiles];
      const uniqueFiles = [];
      const seen = new Set();

      mergedFiles.forEach(file => {
        const key = [file.name, file.size, file.lastModified].join("__");
        if (!seen.has(key)) {
          seen.add(key);
          uniqueFiles.push(file);
        }
      });

      this.manual.arquivos = uniqueFiles;
      this.newFilesPreview = uniqueFiles.map(file => {
        const ext = (file.name.split(".").pop() || "").toLowerCase();
        return {
          icon: ICON_MAP[ext] || "fa-file",
          name: file.name,
          size: this.formatBytes(file.size),
        };
      });

      this.resetManualFileInput();
    },

    removeSelectedFile(index) {
      this.manual.arquivos.splice(index, 1);
      this.newFilesPreview.splice(index, 1);

      if (!this.manual.arquivos.length) {
        this.resetManualFileInput();
      }
    },

    markExistingFileForRemoval(file) {
      if (!file || !file.id) return;

      if (!this.arquivosRemover.includes(file.id)) {
        this.arquivosRemover.push(file.id);
      }

      this.existingFiles = this.existingFiles.filter(existing => existing.id !== file.id);
    },

    onEditorReady(editor) {
      const maybeEditor = editor && typeof editor.getData === "function" ? editor : null;
      this.editorInstance = maybeEditor;

      if (this.editorInstance && this.editorData) {
        this.editorInstance.setData(this.editorData);
      }
    },

    resolveEditorContent(value, editor) {
      if (typeof value === "string") {
        return value;
      }

      if (value && typeof value.getData === "function") {
        return value.getData();
      }

      if (editor && typeof editor.getData === "function") {
        return editor.getData();
      }

      if (this.editorInstance && typeof this.editorInstance.getData === "function") {
        return this.editorInstance.getData();
      }

      return typeof this.editorData === "string" ? this.editorData : (this.manual.conteudo || "");
    },

    onEditorInput(value, editor) {
      const content = this.resolveEditorContent(value, editor);
      this.editorData = content;
      this.manual.conteudo = content;
    },

    getContentForSubmit() {
      const normalize = content => {
        const safe = typeof content === "string" ? content : "";
        const plain = safe
          .replace(/<[^>]*>/g, " ")
          .replace(/&nbsp;/gi, " ")
          .trim();

        return plain.length ? safe : "";
      };

      if (this.editorInstance && typeof this.editorInstance.getData === "function") {
        const fromInstance = normalize(this.editorInstance.getData());
        if (fromInstance) {
          return fromInstance;
        }
      }

      if (typeof this.editorData === "string") {
        const fromModel = normalize(this.editorData);
        if (fromModel) {
          return fromModel;
        }
      }

      const editable = document.querySelector("#manual-editor-wrapper .ck-editor__editable");
      if (editable && typeof editable.innerHTML === "string") {
        const fromDom = normalize(editable.innerHTML);
        if (fromDom) {
          return fromDom;
        }
      }

      return normalize(this.manual.conteudo || "");
    },

    formatBytes(bytes) {
      if (!bytes) return "";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },

    onDispatchAll(value) {
      this._suppressUnitWatch = true;
      if (value) {
        this.manual.unidades = this.todasUnidades.map(u => u.id);
        this.selectedGrupoId = null;
      } else {
        this.manual.unidades = [];
      }
      this.$nextTick(() => {
        this._suppressUnitWatch = false;
      });
    },

    reduceGrupo(option) {
      if (!option) return null;
      return option.id || option.ID || option.grupo_id || null;
    },

    onGrupoInput(grupoId) {
      if (!grupoId) {
        this.manual.unidades = [];
        return;
      }
      this.onGrupoSelected(grupoId);
    },

    async onGrupoSelected(grupoId) {
      try {
        const api = this.$store.state.api + `grupo_items?grupo_id=${grupoId}&paginated=false`;
        const response = await axios.get(api);
        const grupoItens = response.data.data || response.data || [];

        if (!Array.isArray(grupoItens) || !grupoItens.length) {
          this.$message("Aviso", "Grupo selecionado não contém unidades", "warning");
          this.selectedGrupoId = null;
          return;
        }

        const unitIds = Array.from(new Set(
            grupoItens
                .map(item => Number(item.unidade_id))
                .filter(id => !isNaN(id) && id > 0)
        ));

        if (!unitIds.length) {
          this.$message("Aviso", "Grupo selecionado não contém unidades válidas", "warning");
          this.selectedGrupoId = null;
          return;
        }

        this._suppressUnitWatch = true;
        this.manual.unidades = unitIds;
        this.dispatchAll = false;
        this.$nextTick(() => {
          this._suppressUnitWatch = false;
        });
      } catch (e) {
        this.$message("Erro", "Erro ao carregar itens do grupo", "error");
      }
    },

    async handleSubmit(validate) {
      const isValid = await validate();
      if (!isValid) return;
      if (this.contentExceeded) {
        this.$message("Erro", `O conteúdo excede o limite de ${MAX_CONTENT_CHARS} caracteres.`, "error");
        return;
      }
      this.save();
    },

    buildFormData(obj) {
      const formData = new FormData();
      Object.entries(obj).forEach(([key, value]) => {
        if (key === "unidades" && Array.isArray(value)) {
          formData.append("unidades", JSON.stringify(value));
          return;
        }
        if (key === "manual_grupos" && Array.isArray(value)) {
          formData.append("manual_grupos", JSON.stringify(value));
          return;
        }
        if (key === "arquivos" && Array.isArray(value)) {
          value.forEach(file => {
            if (file instanceof File) {
              formData.append("arquivos[]", file);
            }
          });
          return;
        }
        if (key === "arquivos_remover" && Array.isArray(value)) {
          formData.append("arquivos_remover", JSON.stringify(value));
          return;
        }
        if (value !== undefined && value !== null) {
          formData.append(key, value);
        }
      });
      return formData;
    },

    save() {
      let api = this.$store.state.api + "manuais";
      let method = "post";
      if (this.isEdit) {
        api += "/" + this.manualId;
        method = "put";
      }

      if (!this.manual.unidades || !this.manual.unidades.length) {
        this.$message("Erro", "Selecione pelo menos uma unidade", "error");
        return;
      }

      const unidades = this.manual.unidades.map(id => ({unit_id: id}));

      const payload = {
        titulo: this.manual.titulo,
        descricao: this.manual.descricao,
        conteudo: this.getContentForSubmit(),
        manual_grupos: this.manual.manual_grupos,
        unidades,
        status: parseInt(this.manual.status),
      };

      if (this.isEdit && this.arquivosRemover.length) {
        payload.arquivos_remover = this.arquivosRemover;
      }

      let dataToSend = payload;
      const config = {};
      const hasUploadChanges = this.manual.arquivos.length || (this.isEdit && this.arquivosRemover.length);

      if (hasUploadChanges) {
        dataToSend = this.buildFormData({...payload, arquivos: this.manual.arquivos});
        config.headers = {"Content-Type": "multipart/form-data"};
        if (method === "put") {
          dataToSend.append("_method", "PUT");
          method = "post";
        }
      }

      this.isLoading = true;
      axios[method](api, dataToSend, config)
          .then(() => {
            this.$message("Sucesso", "Manual salvo com sucesso", "success");
            this.$router.push("/manuais");
          })
          .catch(error => this.handleApiErrors(error))
          .finally(() => {
            this.isLoading = false;
          });
    },

    handleApiErrors(error) {
      if (!error.response) {
        this.$message("Erro", "Erro de conexão. Verifique sua internet.", "error");
        return;
      }
      const {data, status} = error.response || {};

      if (status === 422) {
        if (data.errors) {
          const messages = [];
          Object.keys(data.errors).forEach(field => {
            const fieldErrors = Array.isArray(data.errors[field]) ? data.errors[field] : [data.errors[field]];
            fieldErrors.forEach(msg => messages.push(msg));
          });
          this.$message("Erros de Validação", messages.join("\n"), "error");
        } else if (data.message) {
          this.$message("Erro", data.message, "error");
        }
        return;
      }

      if (status === 413) {
        this.$message("Erro", "Arquivo excede o limite permitido pelo servidor.", "error");
        this.manual.arquivos = [];
        this.newFilesPreview = [];
        this.resetManualFileInput();
        return;
      }

      if (status === 415) {
        this.$message("Erro", "Formato de arquivo não suportado pelo servidor.", "error");
        return;
      }

      this.$message("Erro", data.message || "Erro ao salvar manual", "error");
    },

    getUnidades() {
      const api = this.$store.state.api + "unidades?paginated=false";
      return axios
          .get(api)
          .then(response => {
            const raw = response.data || [];
            const unidades = Array.isArray(raw) ? raw : (Array.isArray(raw.data) ? raw.data : []);
            this.todasUnidades = unidades.map(u => ({
              id: u.id || u.ID || u.unidade_id,
              nome: u.nome || u.name || u.label || `Unidade ${u.id}`,
            }));
          })
          .catch(() => {
            this.$message("Erro", "Erro ao carregar unidades", "error");
          });
    },

    getGroups() {
      const api = this.$store.state.api + "grupos?paginated=false";
      return axios
          .get(api)
          .then(response => {
            const data = response.data.data || response.data || [];
            this.allGroups = Array.isArray(data)
                ? data.map(group => ({
                  id: group.id || group.ID || group.grupo_id || null,
                  label: group.nome || group.title || group.label || group.name || `Grupo ${group.id || ""}`,
                }))
                : [];
          })
          .catch(() => {
            this.allGroups = [];
          });
    },

    getManualGroups() {
      const api = this.$store.state.api + "manual-grupos/options";
      return axios
          .get(api)
          .then(response => {
            const data = response.data.data || response.data || [];
            this.manualGruposOptions = Array.isArray(data)
                ? data.map(item => ({
                  id: item.id,
                  path: item.path || item.nome || `Grupo ${item.id}`,
                  nome: item.nome || item.path,
                }))
                : [];
          })
          .catch(() => {
            this.manualGruposOptions = [];
          });
    },

    fetchManual(id) {
      const api = this.$store.state.api + "manuais/" + id;

      return axios
          .get(api)
          .then(response => {
            const raw = response.data.data || response.data;
            const manual = Array.isArray(raw) ? raw[0] : raw;

            this.manual.titulo = manual.titulo || "";
            this.manual.descricao = manual.descricao || "";
            this.manual.conteudo = manual.conteudo || "";
            this.editorData = this.manual.conteudo;
            this.manual.manual_grupos = Array.isArray(manual.manual_grupos)
                ? manual.manual_grupos.map(group => group.id)
                : [];
            if (this.editorInstance && typeof this.editorInstance.setData === "function") {
              this.editorInstance.setData(this.editorData);
            }
            this.manual.status = manual.status !== undefined && manual.status !== null ? (manual.status ? 1 : 0) : 1;
            this.manualId = manual.id || this.manualId;
            this.isEdit = !!this.manualId;
            this.manual.arquivos = [];
            this.newFilesPreview = [];
            this.arquivosRemover = [];

            const arquivos = Array.isArray(manual.arquivos) ? manual.arquivos : [];
            this.existingFiles = arquivos.map(arquivo => {
              const ext = (arquivo.extensao || "").toLowerCase();
              return {
                id: arquivo.id,
                icon: ICON_MAP[ext] || "fa-file",
                name: arquivo.arquivo_original || arquivo.arquivo || "Arquivo atual",
                size: arquivo.tamanho ? this.formatBytes(arquivo.tamanho) : "",
                url: arquivo.manual_url || null,
              };
            });

            const associacoes = manual.manuais_unidades || [];
            this._suppressUnitWatch = true;
            this.manual.unidades = associacoes.map(a => a.unidade_id);
            this.$nextTick(() => {
              this._suppressUnitWatch = false;
              if (this.todasUnidades.length && this.manual.unidades.length === this.todasUnidades.length) {
                this.dispatchAll = true;
              }
            });
          })
          .catch(() => {
            this.$message("Erro", "Erro ao carregar dados do manual", "error");
          });
    },

    setLoader(qtd) {
      let load = 0;
      const interval = setInterval(() => {
        if (this.loader < qtd) this.loader++;
        load++;
        if (load >= qtd) clearInterval(interval);
      }, 15);
    },

    initForm() {
      this.manualId = this.$route.params.id;
      this.isEdit = !!this.manualId;

      this.setLoader(30);

      const promises = [this.getUnidades(), this.getGroups(), this.getManualGroups()];

      Promise.all(promises)
          .then(() => {
            if (this.isEdit) {
              return this.fetchManual(this.manualId);
            }
          })
          .finally(() => {
            this.loader = 100;
          });
    },
  },

  mounted() {
    this.initForm();
  },
};
</script>

<style scoped>
.main-content { padding: 0; min-height: 100vh; }
.form-card { border-radius: 12px; box-shadow: 0 2px 20px rgba(0,0,0,0.08); overflow: hidden; margin: 20px; background: #fff; }
.form-content { padding: 30px; }
.form-section { margin-bottom: 25px; }
.form-label { font-weight: 600; color: #2c3e50; margin-bottom: 8px; display: block; font-size: 14px; }
.required { color: #e74c3c; font-weight: bold; }
.form-input, .form-textarea { border-radius: 8px; padding: 12px 16px; font-size: 15px; transition: all 0.3s ease; }
.form-input:focus, .form-textarea:focus { border-color: #667eea; box-shadow: 0 0 0 0.2rem rgba(102,126,234,0.15); }
.form-textarea { resize: vertical; min-height: 90px; }
.form-select-status { border-radius: 8px; padding: 12px 16px; font-size: 15px; min-height: 48px; }

.file-upload-container { border: 2px dashed #dee2e6; border-radius: 8px; padding: 16px; text-align: center; background: #fafafa; }
.file-info { display: block; margin-top: 8px; color: #6c757d; font-size: 13px; }
.file-info i { color: #17a2b8; margin-right: 4px; }

.file-preview-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.file-preview {
  display: flex;
  align-items: center;
  background: #f1f8ff;
  border-left: 4px solid #0366d6;
  padding: 12px 16px;
  border-radius: 4px;
}
.file-preview .fas { color: #0366d6; }
.file-preview-info { flex: 1; margin-left: 8px; }
.file-preview-name { font-weight: 600; color: #2c3e50; word-break: break-all; }
.file-preview-meta { color: #6c757d; font-size: 13px; }

.dispatch-all-box {
  background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
  border: 1px solid #ffeaa7;
  border-radius: 8px;
  padding: 10px 14px;
  min-height: 42px;
  display: flex;
  align-items: center;
}

.form-footer { padding: 25px 30px; margin: 0 -30px -30px -30px; display: flex; justify-content: space-between; align-items: center; border-top: 1px solid #e9ecef; }
.required-info { color: #6c757d; font-size: 13px; }
.form-actions { display: flex; gap: 12px; }
.btn-cancel { background: #6c757d; border: none; color: white; padding: 12px 24px; border-radius: 8px; font-weight: 500; }
.btn-save { border: none; padding: 12px 24px; border-radius: 8px; font-weight: 500; }
.btn-save:disabled { background: #6c757d; opacity: 0.6; }

.loading-container { display: flex; justify-content: center; align-items: center; min-height: 60vh; padding: 20px; }
.loading-card { background: white; border-radius: 12px; padding: 40px; box-shadow: 0 2px 20px rgba(0,0,0,0.08); text-align: center; min-width: 300px; }
.progress { height: 8px; border-radius: 20px; background: #e9ecef; overflow: hidden; margin-bottom: 20px; }
.progress-bar { height: 100%; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); transition: width 0.3s ease; }
.loading-text { color: #2c3e50; }
.loading-title { font-size: 18px; font-weight: 600; margin-bottom: 5px; }
.loading-percentage { font-size: 24px; font-weight: bold; color: #667eea; }

.invalid-feedback { color: #e74c3c; font-size: 13px; margin-top: 5px; font-weight: 500; }

.char-counter { display: block; margin-top: 6px; font-size: 13px; text-align: right; transition: color 0.2s ease; }
.char-counter.counter-normal { color: #6c757d; }
.char-counter.counter-warning { color: #d97706; font-weight: 600; }
.char-counter.counter-danger { color: #e74c3c; font-weight: 700; }
</style>
